import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FolderX } from 'lucide-react';
import type { RunDetail, RunMessageOutcome, RunSummary } from '@shared/types/factory';
import type { FolderGone } from '@shared/folder-gone';
import type { ApiResponse } from '@/lib/api-contract';
import { AppAlert } from '@/components/AppAlert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useLoader } from '@/hooks/use-loader';
import { describeApiError } from '@/lib/api-errors';
import { formatRelative } from '@/lib/formatters';
import { getRun, restoreRunWorktree } from '@/lib/runs-api';
import { runPath } from '@/navigation/project-paths';
import { restoreFailure, restoringRun } from './live-ui';
import { isRunActive, runRef } from './run-status';

const PROGRESS_POLL_MS = 3000;

/**
 * SF-T-273: under a thread's "workspace folder no longer exists" error: Continue in a new worktree, which makes the
 * folder again at the same path (a new branch from the base unless the old one's work isn't merged), sets it up and
 * resends the message that failed. While that goes (`restoring`), what's happening; if it failed (`failure`), what
 * stopped it in plain words, with the setup's log, and Try again. RunFolderGoneCard is the task's Agent tab's (a
 * Follow up run); a task chat passes its own (a turn of the chat).
 */
export function FolderGoneCard({
  gone,
  canContinue,
  onContinue,
  restoring = null,
  failure = null,
  ended = null,
  noPermission = 'Members can continue it in a new worktree.',
  className,
}: {
  gone: FolderGone;
  /** Whoever may write to the agent may continue it. */
  canContinue: boolean;
  /** Continue (or Try again): the request; its error is shown under the button. Not needed while `restoring`. */
  onContinue?: () => Promise<ApiResponse<unknown>>;
  /** While it's being made again: what's going (the run or turn, its latest step). */
  restoring?: ReactNode;
  /** The last Continue failed in a way it knows (restoreFailure). */
  failure?: { setup: boolean; message: string; log: string } | null;
  /** The last try ended in failure some other way: its first line, said rather than hidden. */
  ended?: string | null;
  /** What someone who can't continue sees. */
  noPermission?: string;
  className?: string;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const message = gone.message?.text.trim();

  const press = async () => {
    if (!onContinue) return;
    setPending(true);
    setError(null);
    const res = await onContinue();
    setPending(false);
    if (!res.data) setError(describeApiError(res, "Couldn't continue in a new worktree"));
  };

  if (restoring) {
    return (
      <AppAlert compact tone="info" icon={<Spinner className="size-4" />} title="Making the worktree again" className={className} aria-live="polite">
        <p className="break-all">{gone.path}</p>
        {restoring}
      </AppAlert>
    );
  }

  return (
    <AppAlert
      compact
      tone={failure ? 'critical' : 'warning'}
      icon={<FolderX className="size-4" />}
      title={failure ? (failure.setup ? "The new worktree couldn't be set up" : "The worktree couldn't be made again") : "This thread's worktree folder is gone"}
      className={className}
    >
      <div className="space-y-2">
        {failure ? (
          <>
            <p className="break-words">{failure.message}</p>
            {failure.log ? (
              <details className="rounded border bg-background/60">
                <summary className="cursor-pointer px-2 py-1 text-2xs font-medium">The setup's log</summary>
                <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-all px-2 pb-2 font-mono text-3xs">{failure.log}</pre>
              </details>
            ) : null}
            <p>
              {failure.setup
                ? "Fix what stopped it (on the machine, or in the project's setup), then try again: it sets the worktree it made up again."
                : 'Fix what stopped it on the machine, then try again.'}
            </p>
          </>
        ) : (
          <>
            {ended ? <p className="break-words">{ended}</p> : null}
            <p>It was removed (tidied after a merge, or by hand), so every message to the agent fails.</p>
            <p>
              Continue in a new worktree makes it again at the same path, sets it up and resends the last message with any pictures. It goes on a new
              branch from the base, unless the old branch's work isn't merged yet.
            </p>
          </>
        )}
        {message ? (
          <p className="line-clamp-2 break-words text-2xs">
            The last message: <q>{message}</q>
          </p>
        ) : null}
        {canContinue && onContinue ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={failure ? 'outline' : 'default'}
              className="max-sm:w-full pointer-coarse:h-10"
              loading={pending}
              loadingText="Starting…"
              onClick={() => void press()}
            >
              {failure ? 'Try again' : 'Continue in a new worktree'}
            </Button>
            {error ? <span className="text-xs text-danger-strong">{error}</span> : null}
          </div>
        ) : !canContinue ? (
          <p className="text-2xs">{noPermission}</p>
        ) : null}
      </div>
    </AppAlert>
  );
}

/**
 * The task's Agent tab's: Continue starts a Follow up run (POST /api/runs/:id/restore-worktree) whose first send makes
 * the folder again. While that run goes, its link and latest event (polled); its failure from the run's error.
 */
export function RunFolderGoneCard({
  gone,
  run,
  projectSlug,
  canContinue,
  onStarted,
  restore = restoreRunWorktree,
  detail: given,
  className,
}: {
  gone: FolderGone;
  /** The Agent tab's run: the one Continue is pressed on, or (once pressed) the Follow up making the folder again. */
  run: RunSummary;
  projectSlug: string;
  /** Members and up, as for writing to the agent. */
  canContinue: boolean;
  onStarted?: (outcome: RunMessageOutcome) => void;
  /** Preview only: stands in for the request. */
  restore?: typeof restoreRunWorktree;
  /** Preview only: the restoring run's detail (its latest event), instead of polling it. */
  detail?: RunDetail | null;
  className?: string;
}) {
  const active = isRunActive(run.status);
  const restoring = restoringRun(run);
  const loader = useLoader((signal) => getRun(run.id, { signal, project: projectSlug }), [run.id, projectSlug], {
    enabled: restoring && given === undefined,
    pollMs: PROGRESS_POLL_MS,
    pollWhile: (res) => (res?.data?.run ? isRunActive(res.data.run.status) : true),
  });
  const detail = given === undefined ? loader.data?.data?.run : given;
  const failure = restoreFailure(run);
  // Any other way the run ended in failure (an agent too old to restore, a turn that failed after): said, not hidden.
  const ended = active
    ? `${runRef(run.number)} is still going (${run.live_parked_at ? 'parked, waiting for you' : run.status}), so it can't be continued in a new worktree yet: cancel it from the run's page, then come back here.`
    : !failure && run.status === 'failed' && run.error
      ? `${runRef(run.number)} ended with: ${run.error.trim().split('\n')[0]}`
      : null;
  const last = detail?.events.at(-1);
  return (
    <FolderGoneCard
      gone={gone}
      canContinue={canContinue}
      failure={failure}
      ended={ended}
      className={className}
      onContinue={active ? undefined : async () => {
        const res = await restore(run.id, { project: projectSlug });
        if (res.data) onStarted?.(res.data);
        return res;
      }}
      restoring={
        restoring ? (
          <>
            <p>
              <Link to={runPath(projectSlug, run.number)} className="font-medium hover:underline">
                {runRef(run.number)}
              </Link>{' '}
              makes it again and sets it up (an install and the task's database can take a few minutes), then sends the last message again.
            </p>
            {last ? (
              <p className="text-2xs">
                <span className="tabular-nums">{formatRelative(last.at)}</span> · <span className="break-words">{last.message}</span>
              </p>
            ) : null}
          </>
        ) : null
      }
    />
  );
}
